import { useInfiniteQuery } from 'react-query';
import { TemplateResponse, Template } from '../_types';
import { performanceReportingApiUrl, fetcher, handleError, ServerErrorResponse, createParamString } from 'data_sources/constants';

// Fetch templates from API
export const getTemplates = async (
  params: Record<string, any> = {},
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<TemplateResponse> => {
  try {
    const url = `${performanceReportingApiUrl()}/performance/templates/v1/templates`;

    // Build query parameters
    const queryParams = createParamString(params);
    const fullUrl = queryParams ? `${url}?${queryParams}` : url;

    const response = await fetcher({
      url: fullUrl,
      method: 'GET',
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useTemplates = (
  params: Record<string, any> = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) => {
  return useInfiniteQuery<TemplateResponse, ServerErrorResponse>(
    ['templates', JSON.stringify(params)],
    async ({ pageParam, signal }) => {
      const combinedParams = {
        ...params,
        nextToken: pageParam,
      };
      return await getTemplates(combinedParams, signal, onError);
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
      ...options,
    }
  );
};
